import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  convertDateToExcelNumber,
  entradaProdutoKeys,
  fetchBarcodeModelos,
  fetchListaEstoque,
  fetchListaProdutos,
  fetchRegisteredBarcodes,
  fetchTipoEntradas,
  finalizeEtiquetas,
  insertProductEntries,
} from '@/services/entradaProdutoService';
import type {
  ICurrentProduct,
  IEstoqueItem,
  IParsedBarcode,
  IResumoAgrupado,
  IScannedProduct,
  TBarcodeModel,
} from '@/types/entradaProduto';
import { useAnalytics, useErrors, useLogger, useMetrics } from '@/observability';
import './entrada-produto.css';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';

export const Route = createFileRoute('/_authenticated/movimentacao/entrada-produto/')({
  component: EntradaProdutoPage,
});

const INITIAL_CURRENT_PRODUCT: ICurrentProduct = {
  barcode: '',
  productCode: '',
  description: '',
  lot: '',
  validity: '',
  netWeight: '',
  entryType: 'Produção',
  estoque_destino: 'INDUSTRIA',
  id_estoque_destino: '114',
};

const SCANNED_PRODUCT_COLUMNS = [
  'Código de Barras',
  'Código do Produto',
  'Descrição',
  'Lote',
  'Validade',
  'Peso Líquido',
  'Tipo de Entrada',
  'Integra Lenus',
  'Estoque Destino',
  'Ações',
];

const INPUT_STYLE = {
  border: '2px solid #ddd',
  borderRadius: '5px',
  fontSize: '16px',
  backgroundColor: 'white',
} as const;

function parseModel1(barcode: string): IParsedBarcode {
  const caractermap = barcode.slice(-9);
  const verificacao = caractermap.includes('-');
  if (verificacao) {
    const productCode = barcode.slice(0, -23);
    const validity = `${barcode.slice(-23).substring(0, 2)}-${barcode.slice(-23).substring(2, 4)}-${barcode.slice(-23).substring(4, 8)}`;
    const lot = barcode.slice(-15).substring(0, 8);
    const netWeight = `${parseInt(barcode.slice(-7).substring(0, 1), 10)}.${barcode.slice(-7).substring(1, 3)}`;
    return { productCode, validity, lot, netWeight };
  }

  const caractermap2 = barcode.slice(-10);
  const verificacao2 = caractermap2.includes('-');
  if (verificacao2) {
    const productCode = barcode.slice(0, -24);
    const validity = `${barcode.slice(-24).substring(0, 2)}-${barcode.slice(-24).substring(2, 4)}-${barcode.slice(-24).substring(4, 8)}`;
    const lot = barcode.slice(-16).substring(0, 8);
    const netWeight = `${parseInt(barcode.slice(-8).substring(0, 2), 10)}.${barcode.slice(-8).substring(2, 4)}`;
    return { productCode, validity, lot, netWeight };
  }

  const productCode = barcode.slice(0, -25);
  const validity = `${barcode.slice(-25).substring(0, 2)}-${barcode.slice(-25).substring(2, 4)}-${barcode.slice(-25).substring(4, 8)}`;
  const lot = barcode.slice(-17).substring(0, 8);
  const netWeight = `${parseInt(barcode.slice(-9).substring(0, 3), 10)}.${barcode.slice(-9).substring(3, 5)}`;
  return { productCode, validity, lot, netWeight };
}

function parseBarcode(barcode: string, barcodeModel: TBarcodeModel): IParsedBarcode {
  if (barcodeModel === 'model1') {
    return parseModel1(barcode);
  }
  return {};
}

function getResumoAgrupado(scannedProducts: IScannedProduct[]): IResumoAgrupado[] {
  const map = new Map<string, IResumoAgrupado>();
  scannedProducts.forEach((p) => {
    const cod = p.productCode || '';
    const peso = parseFloat(p.netWeight) || 0;
    if (!map.has(cod)) {
      map.set(cod, { codigo: cod, descricao: p.description || '', qtdCaixas: 0, pesoTotal: 0 });
    }
    const row = map.get(cod)!;
    row.qtdCaixas += 1;
    row.pesoTotal += peso;
  });
  return Array.from(map.values());
}

function getMessageColor(message: string): string {
  if (message.includes('✅')) return 'green';
  if (message.includes('⚠️')) return 'orange';
  if (message.includes('❌')) return 'red';
  return 'blue';
}

function EntradaProdutoPage() {
  const logger = useLogger();
  const metrics = useMetrics();
  const analytics = useAnalytics();
  const errors = useErrors();

  const [currentProduct, setCurrentProduct] = useState<ICurrentProduct>(INITIAL_CURRENT_PRODUCT);
  const [scannedProducts, setScannedProducts] = useState<IScannedProduct[]>([]);
  const [existingBarcodes, setExistingBarcodes] = useState<Set<string>>(new Set());
  const [estoqueFilter, setEstoqueFilter] = useState('');
  const [estoqueDropdownOpen, setEstoqueDropdownOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [barcodeInvalid, setBarcodeInvalid] = useState(false);
  const [barcodeModel, setBarcodeModel] = useState<TBarcodeModel>('');
  const [notaFiscalValue, setNotaFiscalValue] = useState('');
  const [showConfirmFinalize, setShowConfirmFinalize] = useState(false);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const estoqueDropdownRef = useRef<HTMLDivElement>(null);

  const [
    barcodesQuery,
    modelosQuery,
    produtosQuery,
    estoqueQuery,
    tipoEntradasQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: entradaProdutoKeys.registeredBarcodes,
        queryFn: fetchRegisteredBarcodes,
      },
      {
        queryKey: entradaProdutoKeys.barcodeModelos,
        queryFn: fetchBarcodeModelos,
      },
      {
        queryKey: entradaProdutoKeys.produtos,
        queryFn: fetchListaProdutos,
      },
      {
        queryKey: entradaProdutoKeys.estoque,
        queryFn: fetchListaEstoque,
      },
      {
        queryKey: entradaProdutoKeys.tipoEntradas,
        queryFn: fetchTipoEntradas,
      },
    ],
  });

  const productList = produtosQuery.data ?? [];
  const estoqueList = estoqueQuery.data ?? [];
  const tipoEntradaList = tipoEntradasQuery.data ?? [];
  const barcodeModelos = modelosQuery.data ?? [];

  const initialLoading =
    barcodesQuery.isLoading ||
    modelosQuery.isLoading ||
    produtosQuery.isLoading ||
    estoqueQuery.isLoading ||
    tipoEntradasQuery.isLoading;

  const initialLoadError =
    barcodesQuery.isError ||
    modelosQuery.isError ||
    produtosQuery.isError ||
    estoqueQuery.isError ||
    tipoEntradasQuery.isError;

  useEffect(() => {
    if (barcodesQuery.data) {
      setExistingBarcodes(new Set(barcodesQuery.data));
    }
  }, [barcodesQuery.data]);

  useEffect(() => {
    if (tipoEntradaList.length > 0) {
      setCurrentProduct((prev) => ({
        ...prev,
        entryType: prev.entryType || tipoEntradaList[0].descricao,
      }));
    }
  }, [tipoEntradaList]);

  useEffect(() => {
    if (barcodeModelos.length === 0) return;
    setBarcodeModel((prev) => {
      if (prev && barcodeModelos.some((m) => m.value === prev)) return prev;
      const preferred = barcodeModelos.find((m) => m.value === 'model1');
      return preferred?.value ?? barcodeModelos[0].value;
    });
  }, [barcodeModelos]);

  useEffect(() => {
    if (initialLoadError && !message) {
      setMessage(
        '⚠️ Aviso: Alguns dados não puderam ser carregados. A funcionalidade pode estar limitada.',
      );
    }
  }, [initialLoadError, message]);

  const finalizeEtiquetaMutation = useMutation({
    mutationFn: (barcode: string) =>
      finalizeEtiquetas({ etiquetas: [{ codigo_barras: barcode }] }),
  });

  const insertEntriesMutation = useMutation({
    mutationFn: (payload: Parameters<typeof insertProductEntries>[0]) =>
      metrics.measure('entrada_produto.insert', () => insertProductEntries(payload)),
    onSuccess: (result) => {
      analytics.track('Entrada Produto Finalized');
      setMessage(result.message || '✅ Produtos inseridos com sucesso!');
      setScannedProducts([]);
      setNotaFiscalValue('');
      setShowConfirmFinalize(false);
    },
    onError: (error: Error) => {
      errors.capture(error, { tags: { source: 'entrada-produto.insert' } });
      setMessage(`❌ Erro ao inserir produtos: ${error.message}`);
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentProduct((prev) => ({ ...prev, [name]: value }));
    if (name === 'barcode') {
      setBarcodeInvalid(false);
    }
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBarcodeModel(e.target.value);
  };

  const handleEntryTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentProduct((prev) => ({
      ...prev,
      entryType: e.target.value,
    }));
  };

  const handleEstoqueSelect = (item: IEstoqueItem) => {
    setCurrentProduct((prev) => ({
      ...prev,
      id_estoque_destino: String(item.codigo),
      estoque_destino: item.descricao || '',
    }));
    setEstoqueFilter('');
    setEstoqueDropdownOpen(false);
  };

  const estoqueFiltered = estoqueFilter.trim()
    ? estoqueList.filter(
        (item) =>
          (item.descricao || '').toLowerCase().includes(estoqueFilter.toLowerCase()) ||
          String(item.codigo || '').includes(estoqueFilter.trim()),
      )
    : estoqueList;

  const handleClearFields = () => {
    setCurrentProduct({
      ...INITIAL_CURRENT_PRODUCT,
      entryType: currentProduct.entryType || tipoEntradaList[0]?.descricao || '',
      estoque_destino: currentProduct.estoque_destino,
      id_estoque_destino: currentProduct.id_estoque_destino,
    });
    setNotaFiscalValue('');
    setEstoqueFilter('');
    setMessage('');
    setBarcodeInvalid(false);
    barcodeInputRef.current?.focus();
  };

  const handleAddProduct = async () => {
    if (!currentProduct.barcode || currentProduct.barcode.trim() === '') {
      setMessage('⚠️ Por favor, insira ou escaneie um código de barras.');
      setBarcodeInvalid(true);
      barcodeInputRef.current?.focus();
      return;
    }
    setBarcodeInvalid(false);

    if (!barcodeModel) {
      setMessage('⚠️ Aguarde o carregamento do modelo de código de barras antes de adicionar.');
      return;
    }

    if (!currentProduct.id_estoque_destino || !currentProduct.estoque_destino) {
      setMessage('⚠️ Por favor, selecione o estoque destino.');
      return;
    }

    const barcodeExistsInList = scannedProducts.some(
      (product) => product.barcode === currentProduct.barcode,
    );

    if (barcodeExistsInList) {
      setMessage('❌ Erro: Código de barras já existe na lista atual.');
      return;
    }

    if (existingBarcodes.has(currentProduct.barcode)) {
      if (currentProduct.entryType === 'Devolução') {
        try {
          await finalizeEtiquetaMutation.mutateAsync(currentProduct.barcode);
          setMessage(
            `✅ Etiqueta ${currentProduct.barcode} atualizada para DISPONIVEL com sucesso.`,
          );
        } catch (error) {
          errors.capture(error, { tags: { source: 'entrada-produto.devolucao' } });
          setMessage(`❌ Erro ao atualizar o status da etiqueta ${currentProduct.barcode}.`);
        }
        return;
      }
      setMessage(
        '❌ Erro: Código de barras já existe no banco de dados. Use "Devolução" para reativar.',
      );
      return;
    }

    if (
      currentProduct.entryType === 'Recebimento' &&
      (!notaFiscalValue || notaFiscalValue.trim() === '')
    ) {
      setMessage('⚠️ Por favor, insira o número da nota fiscal para recebimento.');
      return;
    }

    const parsedData = parseBarcode(currentProduct.barcode, barcodeModel);

    logger.debug('Barcode parsed', {
      barcode: currentProduct.barcode,
      productCode: parsedData.productCode,
      model: barcodeModel,
    });

    if (!parsedData.productCode) {
      setMessage('❌ Erro: Código de barras inválido ou formato não reconhecido.');
      setBarcodeInvalid(true);
      return;
    }

    // Legado: match em produto_referencia (sync → produtos.referencia), não no nome.
    const product = productList.find(
      (item) =>
        item.referencia === parsedData.productCode ||
        item.nome === parsedData.productCode,
    );

    if (product) {
      const updatedProduct: IScannedProduct = {
        ...currentProduct,
        ...parsedData,
        description: product.descricao,
        id_agrupador_categoria: product.agrupador_externo ?? null,
        agrupador_externo: product.agrupador_externo ?? null,
        integraLenus: true,
      };

      setScannedProducts((prev) => {
        const updatedProducts = [...prev, updatedProduct];
        setMessage(`✅ Produto adicionado com sucesso! (${updatedProducts.length} caixas)`);
        return updatedProducts;
      });

      setExistingBarcodes((prev) => new Set(prev).add(currentProduct.barcode));
    } else {
      setMessage(
        `❌ Erro: Produto com referência ${parsedData.productCode} não encontrado na lista de produtos.`,
      );
    }

    setCurrentProduct({
      barcode: '',
      productCode: '',
      description: '',
      lot: '',
      validity: '',
      netWeight: '',
      entryType: currentProduct.entryType,
      estoque_destino: currentProduct.estoque_destino,
      id_estoque_destino: currentProduct.id_estoque_destino,
    });
    barcodeInputRef.current?.focus();
  };

  const handleRemoveProduct = (index: number) => {
    setScannedProducts((prev) => {
      const removedProduct = prev[index];
      const updatedProducts = prev.filter((_, i) => i !== index);

      if (removedProduct?.barcode) {
        setExistingBarcodes((prevBarcodes) => {
          const newBarcodes = new Set(prevBarcodes);
          newBarcodes.delete(removedProduct.barcode);
          return newBarcodes;
        });
      }

      setMessage(`✅ Produto removido da lista. (${updatedProducts.length} caixas restantes)`);
      return updatedProducts;
    });
  };

  const handleFinalizeClick = () => {
    if (scannedProducts.length === 0) {
      setMessage('⚠️ Nenhum produto para finalizar. Adicione produtos primeiro.');
      return;
    }
    const recebimentoProducts = scannedProducts.filter((p) => p.entryType === 'Recebimento');
    if (recebimentoProducts.length > 0 && (!notaFiscalValue || notaFiscalValue.trim() === '')) {
      setMessage('⚠️ Produtos de recebimento encontrados. Insira o número da nota fiscal.');
      return;
    }
    setShowConfirmFinalize(true);
  };

  const handleConfirmFinalize = () => {
    const currentDate = new Date();
    const formattedCurrentDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${currentDate.getFullYear()}`;

    const productsWithConvertedDate = scannedProducts.map((product) => ({
      ...product,
      validity: convertDateToExcelNumber(product.validity),
      dataentrada: convertDateToExcelNumber(formattedCurrentDate),
      numero_nf: product.entryType === 'Recebimento' ? notaFiscalValue : null,
      estoque_destino: product.estoque_destino,
      id_estoque_destino: product.id_estoque_destino || null,
    }));

    insertEntriesMutation.mutate({ products: productsWithConvertedDate });
  };

  const handleBarcodeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      void handleAddProduct();
    }
  };

  const resumoAgrupado = getResumoAgrupado(scannedProducts);
  const totaisGeral = resumoAgrupado.reduce(
    (acc, r) => ({ qtdCaixas: acc.qtdCaixas + r.qtdCaixas, pesoTotal: acc.pesoTotal + r.pesoTotal }),
    { qtdCaixas: 0, pesoTotal: 0 },
  );

  const finalizeSubmitting = insertEntriesMutation.isPending;

  return (
    <div className="entrada-produto-container">
      <div className="entrada-produto-content">
        {message ? (
          <p
            id="entrada-produto-message"
            className="entrada-produto-message"
            style={{ color: getMessageColor(message) }}
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        ) : null}
        <Card className="entrada-card">
          <Card.Content>
            <form className="entrada-produto-form-grid">
              <div className="input-content entrada-produto-form-grid__field">
                <label htmlFor="barcode">Código de Barras</label>

                <input
                  type="text"
                  id="barcode"
                  name="barcode"
                  ref={barcodeInputRef}
                  value={currentProduct.barcode}
                  onChange={handleInputChange}
                  onKeyDown={handleBarcodeInputKeyDown}
                  placeholder="Escaneie ou digite"
                  style={INPUT_STYLE}
                  aria-invalid={barcodeInvalid}
                  aria-describedby={message ? 'entrada-produto-message' : undefined}
                />
              </div>

              <div className="input-content entrada-produto-form-grid__field">
                <label htmlFor="model">Modelo de Código de Barras</label>

                <select
                  id="model"
                  value={barcodeModel}
                  onChange={handleModelChange}
                  style={{ ...INPUT_STYLE, cursor: 'pointer' }}
                >
                  {barcodeModelos.length === 0 ? (
                    <option value="">Carregando...</option>
                  ) : (
                    barcodeModelos.map((modelo) => (
                      <option
                        key={modelo.id}
                        value={modelo.value}
                      >
                        {modelo.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="input-content entrada-produto-form-grid__field">
                <label htmlFor="entryType">Tipo de Entrada</label>

                <select
                  id="entryType"
                  name="entryType"
                  value={currentProduct.entryType}
                  onChange={handleEntryTypeChange}
                  style={{ ...INPUT_STYLE, cursor: 'pointer' }}
                >
                  {tipoEntradaList.length === 0 ? (
                    <option value="">Carregando...</option>
                  ) : (
                    tipoEntradaList.map((tipo) => (
                      <option
                        key={tipo.id}
                        value={tipo.descricao}
                      >
                        {tipo.descricao}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div
                className="input-content entrada-produto-form-grid__field entrada-produto-form-grid__field--dropdown"
                ref={estoqueDropdownRef}
              >
                <label htmlFor="estoque_destino">Estoque Destino</label>
                <input
                  id="estoque_destino"
                  type="text"
                  autoComplete="off"
                  placeholder="Digite para filtrar ou selecione"
                  value={
                    estoqueDropdownOpen || estoqueFilter !== ''
                      ? estoqueFilter
                      : currentProduct.estoque_destino
                  }
                  onChange={(e) => {
                    setEstoqueFilter(e.target.value);
                    setEstoqueDropdownOpen(true);
                  }}
                  onFocus={() => setEstoqueDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setEstoqueDropdownOpen(false), 200)}
                  style={{ ...INPUT_STYLE, width: '100%', boxSizing: 'border-box' }}
                />
                {estoqueDropdownOpen && (
                  <ul className="entrada-produto-estoque-dropdown">
                    {estoqueFiltered.length === 0 ? (
                      <li className="entrada-produto-estoque-dropdown__empty">Nenhum estoque encontrado</li>
                    ) : (
                      estoqueFiltered.map((item) => (
                        <li
                          key={String(item.codigo)}
                          className={
                            currentProduct.id_estoque_destino === String(item.codigo)
                              ? 'entrada-produto-estoque-dropdown__item entrada-produto-estoque-dropdown__item--selected'
                              : 'entrada-produto-estoque-dropdown__item'
                          }
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleEstoqueSelect(item);
                          }}
                        >
                          {item.descricao}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>

              {currentProduct.entryType === 'Recebimento' && (
                <div className="input-content entrada-produto-form-grid__field entrada-produto-form-grid__field--row-2-col-1">
                  <label htmlFor="NotaFiscal">Insira o Número da Nota Fiscal</label>
                  <input
                    id="NotaFiscal"
                    type="text"
                    value={notaFiscalValue}
                    onChange={(e) => setNotaFiscalValue(e.target.value)}
                    style={INPUT_STYLE}
                  />
                </div>
              )}
            </form>

            <div
              className="filter-card__divider"
              aria-hidden="true"
            />
          </Card.Content>

          <Card.Footer>
            <button
              className="clear-filter-button"
              type="button"
              onClick={handleClearFields}
            >
              Limpar Campos
            </button>

            <button
              className="filter-button"
              type="button"
              onClick={() => void handleAddProduct()}
            >
              Adicionar Produto
            </button>
          </Card.Footer>
        </Card>

        <div className="controle-estoque-table-container">
          <h3>Produtos Escaneados</h3>

          <div className="controle-estoque-table-scroll">
            <Table className="estoque-table">
              <caption className="sr-only">Produtos escaneados</caption>
              <Table.Header>
                <Table.Row>
                  {SCANNED_PRODUCT_COLUMNS.map((column, index) => (
                    <Table.Head
                      key={index}
                      scope="col"
                    >
                      {column}
                    </Table.Head>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Content>
                {scannedProducts.map((product, index) => (
                  <Table.Row key={`${product.barcode}-${index}`}>
                    <Table.Cell>{product.barcode}</Table.Cell>
                    <Table.Cell>{product.productCode}</Table.Cell>
                    <Table.Cell>{product.description}</Table.Cell>
                    <Table.Cell>{product.lot}</Table.Cell>
                    <Table.Cell>{product.validity}</Table.Cell>
                    <Table.Cell>{product.netWeight}</Table.Cell>
                    <Table.Cell>{product.entryType}</Table.Cell>
                    <Table.Cell>{product.integraLenus === false ? 'Não' : 'Sim'}</Table.Cell>
                    <Table.Cell>{product.estoque_destino}</Table.Cell>
                    <Table.Cell>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(index)}
                      >
                        Remover
                      </button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Content>
              <Table.Footer>
                <Table.Row>
                  <Table.Cell
                    colSpan={SCANNED_PRODUCT_COLUMNS.length}
                    className="entrada-produto-table-footer-cell"
                  >
                    <button
                      type="button"
                      className="filter-button"
                      onClick={handleFinalizeClick}
                      disabled={scannedProducts.length === 0 || initialLoading}
                    >
                      Finalizar
                    </button>
                  </Table.Cell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </div>
        </div>
      </div>

      {showConfirmFinalize && (
        <Modal
          className="entrada-produto-finalize-modal"
          onClose={() => !finalizeSubmitting && setShowConfirmFinalize(false)}
          closeOnOverlayClick={!finalizeSubmitting}
          closeOnEscape={!finalizeSubmitting}
        >
          <Modal.Header
            title="Confirmar finalização"
            showCloseButton={!finalizeSubmitting}
          />
          <Modal.Content>
            <p className="entrada-produto-finalize-modal__subtitle">Resumo da entrada</p>
            <Table>
              <caption className="sr-only">Resumo da entrada de produtos</caption>
              <Table.Header>
                <Table.Row>
                  <Table.Head scope="col">Código</Table.Head>
                  <Table.Head scope="col">Qtd. caixas</Table.Head>
                  <Table.Head scope="col">Peso total (kg)</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Content>
                {resumoAgrupado.map((r) => (
                  <Table.Row key={r.codigo}>
                    <Table.Cell>{r.codigo}</Table.Cell>
                    <Table.Cell>{r.qtdCaixas}</Table.Cell>
                    <Table.Cell>{r.pesoTotal.toFixed(3)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Content>
              <Table.Footer>
                <Table.Row>
                  <Table.Cell>
                    <strong>Total</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{totaisGeral.qtdCaixas}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{totaisGeral.pesoTotal.toFixed(3)}</strong>
                  </Table.Cell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </Modal.Content>
          <Modal.Footer>
            <button
              type="button"
              className="clear-filter-button"
              onClick={() => !finalizeSubmitting && setShowConfirmFinalize(false)}
              disabled={finalizeSubmitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="filter-button"
              onClick={handleConfirmFinalize}
              disabled={finalizeSubmitting}
            >
              {finalizeSubmitting ? 'Enviando…' : 'Confirmar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
